<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useEventListener } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { CopilotChat, CopilotThreadList } from 'shared/components/copilot';
import { useCopilotStore } from 'dashboard/stores/copilot';
import CopilotSidePanel from 'shared/components/copilot/CopilotSidePanel.vue';
import { panelKey } from 'shared/helpers/copilotMentionContext';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const accountId = computed(() => route.params.accountId);
const chatRef = ref(null);
const store = useCopilotStore();
const { sidebarOpen, activeThreadId } = storeToRefs(store);
const assistantType = ref('copilot');

// Restore thread from URL synchronously so CopilotChat picks it up on mount
const threadFromUrl = route.query.thread;
if (threadFromUrl) {
  store.setActiveThread(threadFromUrl);
}

// Sync activeThreadId → URL query param (handles both user selection and new-thread creation)
watch(activeThreadId, id => {
  const current = route.query.thread;
  if (id && id !== current) {
    router.replace({ query: { ...route.query, thread: id } });
  } else if (!id && current) {
    const q = { ...route.query };
    delete q.thread;
    router.replace({ query: q });
  }
});

// Side-panel state (Phase 6)
const activePanels = ref([]);
const activePanelIndex = ref(0);

function handleSelectThread(threadId) {
  activePanels.value = [];
  activePanelIndex.value = 0;
  chatRef.value?.selectThread(threadId);
}

function handleNewThread() {
  activePanels.value = [];
  activePanelIndex.value = 0;
  chatRef.value?.newThread();
}

function handleOpenPanel(panel) {
  const key = panelKey(panel);
  const existingIndex = activePanels.value.findIndex(p => panelKey(p) === key);
  if (existingIndex >= 0) {
    activePanelIndex.value = existingIndex;
    return;
  }
  activePanels.value.push(panel);
  activePanelIndex.value = activePanels.value.length - 1;
}

function handleClosePanel(index) {
  activePanels.value.splice(index, 1);
  if (activePanelIndex.value >= activePanels.value.length) {
    activePanelIndex.value = Math.max(0, activePanels.value.length - 1);
  }
}

const PANEL_WIDTH_KEY = 'copilotSidePanelWidth';
const DEFAULT_PANEL_RATIO = 0.6;
const MIN_PANEL_WIDTH = 420;
const MIN_CHAT_WIDTH = 320;

const splitRef = ref(null);
const isResizing = ref(false);
const resizeStartX = ref(0);
const resizeStartWidth = ref(0);
const panelWidth = ref(Number(localStorage.getItem(PANEL_WIDTH_KEY)) || 0);

function splitWidth() {
  return splitRef.value?.clientWidth || 0;
}

function clampPanelWidth(width) {
  const available = splitWidth();
  if (!available) return Math.max(MIN_PANEL_WIDTH, width);
  const maxWidth = Math.max(MIN_PANEL_WIDTH, available - MIN_CHAT_WIDTH);
  return Math.round(Math.min(maxWidth, Math.max(MIN_PANEL_WIDTH, width)));
}

function defaultPanelWidth() {
  return clampPanelWidth(splitWidth() * DEFAULT_PANEL_RATIO);
}

function ensurePanelWidth() {
  panelWidth.value = clampPanelWidth(panelWidth.value || defaultPanelWidth());
}

watch(
  () => activePanels.value.length,
  len => {
    if (len) ensurePanelWidth();
  }
);

const onResizeStart = event => {
  isResizing.value = true;
  resizeStartX.value = event.clientX;
  resizeStartWidth.value = panelWidth.value || defaultPanelWidth();
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;
  const delta = resizeStartX.value - event.clientX;
  panelWidth.value = clampPanelWidth(resizeStartWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;
  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });
  localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth.value));
};

const onResizeReset = () => {
  panelWidth.value = defaultPanelWidth();
  localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth.value));
};

useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(window, 'resize', () => {
  if (activePanels.value.length) ensurePanelWidth();
});

// Expose helpers on window for dev/testing
onMounted(() => {
  window.copilot = {
    openEventPanel: id => handleOpenPanel({ type: 'event', id: String(id) }),
    openConversationPanel: id =>
      handleOpenPanel({ type: 'conversation', id: String(id) }),
    openBookingWidgetPanel: id =>
      handleOpenPanel({ type: 'booking_widget', id: String(id) }),
    openReportPanel: data =>
      handleOpenPanel({ type: 'report', id: String(Date.now()), data }),
    closePanel: index => handleClosePanel(index ?? 0),
    closePanels: () => {
      activePanels.value = [];
    },
    get panels() {
      return activePanels.value;
    },
  };
});

onBeforeUnmount(() => {
  delete window.copilot;
});
</script>

<template>
  <div class="flex h-full w-full">
    <!-- Thread sidebar (collapsed by default) -->
    <CopilotThreadList
      v-if="sidebarOpen"
      :account-id="accountId"
      @select-thread="handleSelectThread"
      @new-thread="handleNewThread"
    />

    <div ref="splitRef" class="flex flex-1 min-w-0 h-full">
      <!-- Chat area -->
      <div class="flex flex-col flex-1 min-w-[320px] min-h-0">
        <CopilotChat
          :key="assistantType"
          ref="chatRef"
          :account-id="accountId"
          :assistant-type="assistantType"
          @update:assistant-type="
            val => {
              assistantType = val;
            }
          "
          @open-panel="handleOpenPanel"
        />
      </div>

      <!-- Side panels (Phase 6) -->
      <template v-if="activePanels.length">
        <div
          class="relative w-1.5 shrink-0 cursor-col-resize z-40 group"
          :title="t('COPILOT.PANEL.RESIZE')"
          @mousedown="onResizeStart"
          @dblclick="onResizeReset"
        >
          <div
            class="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-n-weak group-hover:w-0.5 group-hover:bg-n-brand transition-[width,background-color]"
            :class="{ 'w-0.5 bg-n-brand': isResizing }"
          />
        </div>
        <CopilotSidePanel
          v-model:active-index="activePanelIndex"
          class="shrink-0"
          :class="{ 'pointer-events-none': isResizing }"
          :style="{ width: `${panelWidth}px` }"
          :panels="activePanels"
          :account-id="accountId"
          @close="handleClosePanel"
          @close-all="activePanels = []"
        />
      </template>
    </div>
  </div>
</template>
